// Canonical role codes. Keep in sync with:
//   - backend/internal/platform/auth/roles.go
//   - backend/migrations/0013_normalize_staff_roles.up.sql (CHECK constraint)
//
// All RBAC checks (feature `permissions.ts` files) compare staff.role against
// these codes. Legacy Vietnamese values (e.g. "GĐ Điều Hành") are not accepted —
// migration 0013 normalises pre-existing data and the Appwrite migrator
// (cmd/migrate-appwrite/transformer_staff.go) maps on insert.

// Values are ARCHETYPE codes. Casdoor stores app-scoped roles (`gnpf-*`);
// the backend strips the prefix so the FE always sees a bare archetype
// (decision 0010).
export const ROLE = {
  Admin: "admin",
  Director: "director",
  Manager: "manager",
  Coordinator: "coordinator",
  Accounting: "accounting",
  Sale: "sale",
  Staff: "staff",
  Viewer: "viewer",
  // Vai chuỗi (decision 0036, 20/08/2026). Địa bàn KHÔNG nằm trong role —
  // gán qua Casdoor branch scope trên màn Nhân sự; chưa gán là không thấy gì.
  BranchManager: "branch_manager",
  AreaManager: "area_manager",
  QC: "qc",
  // Trưởng ca (quyết định 0209, 25/09/2026). Cầm đúng một quyền: đổi gán
  // suất của máy chia khi thực tế trượt khỏi dự tính. Địa bàn cũng gán qua
  // Casdoor branch scope như ba vai chuỗi trên.
  ShiftLead: "shift_lead",
} as const;

export type Role = (typeof ROLE)[keyof typeof ROLE];

export const ALL_ROLES: readonly Role[] = [
  ROLE.Admin,
  ROLE.Director,
  ROLE.Manager,
  ROLE.Coordinator,
  ROLE.Accounting,
  ROLE.Sale,
  ROLE.Staff,
  ROLE.Viewer,
  ROLE.BranchManager,
  ROLE.AreaManager,
  ROLE.QC,
  ROLE.ShiftLead,
];

/**
 * Hai vai đã khoá — Điều phối (QĐ 0050) và Trưởng ca (QĐ 0228): 0 quyền, 0
 * trang. Từ 17/10/2026 (CEO chốt Q02) máy chủ từ chối mọi lượt gọi của hai vai
 * này (`auth.IsLockedRole`, mã lỗi `account_role_locked`) và app chỉ hiện màn
 * "Tài khoản đã khoá".
 */
export const LOCKED_ROLES: readonly Role[] = [ROLE.Coordinator, ROLE.ShiftLead];

export function isLockedRole(role: string | null | undefined): boolean {
  return (LOCKED_ROLES as readonly string[]).includes((role ?? "").trim().toLowerCase());
}

export function isValidRole(s: string | null | undefined): s is Role {
  return !!s && (ALL_ROLES as readonly string[]).includes(s);
}
